import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { 
  Eye, 
  MousePointerClick, 
  TrendingUp, 
  Euro, 
  Phone, 
  ChevronDown,
  FileText,
  Info,
  Pin
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardAnzeigen = () => {
  const [campaignsData, setCampaignsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7days');

  useEffect(() => {
    fetchAdCopy();
  }, [period]);

  const fetchAdCopy = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/google-ads/ad-copy?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCampaignsData(response.data.campaigns || []);
    } catch (error) {
      console.error('Error fetching ad copy:', error);
      if (error.response?.status !== 404) {
        toast.error('Fehler beim Laden der Anzeigen');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'ENABLED':
        return 'default';
      case 'PAUSED':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'ENABLED':
        return '🟢 Aktiv';
      case 'PAUSED':
        return '⏸️ Pausiert';
      case 'REMOVED':
        return '⚫ Deaktiviert';
      default:
        return status;
    }
  };

  const getPeriodLabel = (period) => {
    switch (period) {
      case '7days':
        return 'Letzte 7 Tage';
      case 'this_month':
        return 'Dieser Monat';
      case 'last_month':
        return 'Vorheriger Monat';
      case 'all_time':
        return 'Gesamtzeitraum';
      default:
        return period;
    }
  };

  const formatCost = (costMicros) => {
    return (costMicros / 1000000).toFixed(2);
  };

  const getPinningText = (pinnedPosition) => {
    if (!pinnedPosition || pinnedPosition === 'UNSPECIFIED' || pinnedPosition === 'UNKNOWN') {
      return null;
    }
    
    // Convert enum like "HEADLINE_1" to "Position 1"
    const match = pinnedPosition.match(/(\d+)/);
    if (match) {
      return `Position ${match[1]}`;
    }
    
    return 'Gepinnt';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0040FF] mx-auto mb-4"></div>
          <p className="text-gray-600">Anzeigen werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header mit Zeitraum-Auswahl */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Werbeanzeigen</h1>
          <p className="text-gray-600">
            Live-Anzeigentexte aus Ihren Google Ads Kampagnen
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Zeitraum:</span>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Zeitraum wählen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7days">Letzte 7 Tage</SelectItem>
              <SelectItem value="this_month">Dieser Monat</SelectItem>
              <SelectItem value="last_month">Vorheriger Monat</SelectItem>
              <SelectItem value="all_time">Gesamtzeitraum</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Content */}
      {campaignsData.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="text-gray-400 mb-4">
              <FileText className="w-16 h-16" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Keine Google Ads Kampagnen gefunden
            </h3>
            <p className="text-gray-600 text-center max-w-md">
              Es wurden noch keine Google Ads Kampagnen mit Ihrem Account verknüpft.
              Kontaktieren Sie uns, um Ihre Kampagnen zu aktivieren.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {campaignsData.map((campaign) => (
            <div key={campaign.campaign_id} className="space-y-4">
              {/* Kampagnen-Header */}
              <Card className="bg-gray-50">
                <CardHeader className="pb-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <CardTitle className="text-xl">{campaign.campaign_name}</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">
                        {campaign.ads?.length || 0} {campaign.ads?.length === 1 ? 'Anzeige' : 'Anzeigen'}
                      </p>
                    </div>
                    <Badge variant={getStatusVariant(campaign.campaign_status)}>
                      {getStatusText(campaign.campaign_status)}
                    </Badge>
                  </div>
                </CardHeader>
              </Card>

              {/* Anzeigen Liste */}
              {campaign.ads && campaign.ads.length > 0 ? (
                <div className="space-y-4 ml-0 sm:ml-4">
                  {campaign.ads.map((ad, adIndex) => (
                    <AdCard 
                      key={ad.id} 
                      ad={ad} 
                      adIndex={adIndex + 1}
                      getStatusText={getStatusText}
                      getStatusVariant={getStatusVariant}
                      formatCost={formatCost}
                      getPinningText={getPinningText}
                    />
                  ))}
                </div>
              ) : (
                <Card className="ml-0 sm:ml-4">
                  <CardContent className="py-8">
                    <div className="flex items-start gap-3 text-sm text-gray-600">
                      <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-900 mb-1">
                          Für diese Kampagne wurden noch keine Google Ads Anzeigen gefunden.
                        </p>
                        <p>
                          Dies kann bei neuen Kampagnen der Fall sein. Die Daten werden täglich um 6:00 Uhr synchronisiert.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// AdCard Component
const AdCard = ({ ad, adIndex, getStatusText, getStatusVariant, formatCost, getPinningText }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAllHeadlines, setShowAllHeadlines] = useState(false);
  const [showAllDescriptions, setShowAllDescriptions] = useState(false);

  const hasConversions = ad.conversions > 0;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className="w-full">
          <CardHeader className="cursor-pointer">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-gray-600" />
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">
                    Suchanzeige {adIndex}
                  </h3>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {ad.ad_group_name}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant={getStatusVariant(ad.status)} className="hidden sm:flex">
                  {getStatusText(ad.status)}
                </Badge>
                <ChevronDown 
                  className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CardContent className="pt-0">
          {/* Compact Stats - immer sichtbar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <StatItem 
              icon={Eye} 
              label="Impressionen" 
              value={ad.impressions?.toLocaleString('de-DE') || '0'} 
            />
            <StatItem 
              icon={MousePointerClick} 
              label="Klicks" 
              value={ad.clicks?.toLocaleString('de-DE') || '0'} 
            />
            <StatItem 
              icon={TrendingUp} 
              label="CTR" 
              value={`${ad.ctr?.toFixed(2) || '0.00'}%`} 
            />
            <StatItem 
              icon={Euro} 
              label="Kosten" 
              value={`€${formatCost(ad.cost_micros || 0)}`} 
            />
          </div>

          {/* Anfragen - nur wenn > 0 */}
          {hasConversions && (
            <div className="p-3 bg-green-50 rounded-lg mb-4 flex items-center gap-2">
              <Phone className="w-4 h-4 text-green-600" />
              <span className="font-semibold text-green-700">
                {ad.conversions} {ad.conversions === 1 ? 'Anfrage' : 'Anfragen'}
              </span>
            </div>
          )}

          {/* Expanded Content */}
          <CollapsibleContent>
            <Separator className="my-4" />
            
            <div className="space-y-6">
              {/* Headlines */}
              {ad.headlines && ad.headlines.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    📌 Anzeigentitel ({ad.headlines.length})
                  </h4>
                  <div className="space-y-2">
                    {(showAllHeadlines ? ad.headlines : ad.headlines.slice(0, 5)).map((headline, idx) => {
                      const pinning = getPinningText(headline.pinned_position);
                      return (
                        <div 
                          key={idx} 
                          className="p-3 bg-gray-50 rounded-lg text-sm flex items-start justify-between gap-2"
                        >
                          <span className="flex-1">{headline.text}</span>
                          {pinning && (
                            <Badge variant="secondary" className="flex items-center gap-1 text-xs">
                              <Pin className="w-3 h-3" />
                              {pinning}
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                    {ad.headlines.length > 5 && !showAllHeadlines && (
                      <button
                        onClick={() => setShowAllHeadlines(true)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        +{ad.headlines.length - 5} weitere anzeigen
                      </button>
                    )}
                    {showAllHeadlines && ad.headlines.length > 5 && (
                      <button
                        onClick={() => setShowAllHeadlines(false)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Weniger anzeigen
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Descriptions */}
              {ad.descriptions && ad.descriptions.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    📄 Beschreibungen ({ad.descriptions.length})
                  </h4>
                  <div className="space-y-2">
                    {(showAllDescriptions ? ad.descriptions : ad.descriptions.slice(0, 2)).map((desc, idx) => {
                      const pinning = getPinningText(desc.pinned_position);
                      return (
                        <div 
                          key={idx} 
                          className="p-3 bg-gray-50 rounded-lg text-sm flex items-start justify-between gap-2"
                        >
                          <span className="flex-1">{desc.text}</span>
                          {pinning && (
                            <Badge variant="secondary" className="flex items-center gap-1 text-xs">
                              <Pin className="w-3 h-3" />
                              {pinning}
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                    {ad.descriptions.length > 2 && !showAllDescriptions && (
                      <button
                        onClick={() => setShowAllDescriptions(true)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        +{ad.descriptions.length - 2} weitere anzeigen
                      </button>
                    )}
                    {showAllDescriptions && ad.descriptions.length > 2 && (
                      <button
                        onClick={() => setShowAllDescriptions(false)}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Weniger anzeigen
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Last Synced Info */}
              <div className="text-xs text-gray-500 flex items-center gap-1">
                <Info className="w-3 h-3" />
                Zuletzt synchronisiert: {new Date(ad.last_synced_at).toLocaleString('de-DE')}
              </div>
            </div>
          </CollapsibleContent>
        </CardContent>
      </Collapsible>
    </Card>
  );
};

// StatItem Component
const StatItem = ({ icon: Icon, label, value }) => (
  <div className="flex flex-col">
    <div className="flex items-center gap-1.5 text-gray-600 mb-1">
      <Icon className="w-4 h-4" />
      <span className="text-xs">{label}</span>
    </div>
    <span className="font-semibold text-gray-900">{value}</span>
  </div>
);

export default DashboardAnzeigen;
